/**
 * Badge Component - Phase 2C-1
 * 
 * Status and category badges with neutral color variants.
 * BSL Compliance: No red/green sentiment colors.
 */

import React from 'react';

export type BadgeVariant = 
  | 'default'
  | 'active'
  | 'pending'
  | 'complete'
  | 'locked'
  | 'mode-gm'
  | 'mode-pick'
  | 'info'
  | 'neutral';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-slate-700 text-slate-200',
  active: 'bg-blue-600/20 text-blue-400 border border-blue-500/30',
  pending: 'bg-amber-600/20 text-amber-400 border border-amber-500/30',
  complete: 'bg-slate-600/20 text-slate-400 border border-slate-500/30',
  locked: 'bg-purple-600/20 text-purple-400 border border-purple-500/30',
  'mode-gm': 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30',
  'mode-pick': 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30',
  info: 'bg-blue-600/20 text-blue-400 border border-blue-500/30',
  neutral: 'bg-slate-600/20 text-slate-300 border border-slate-500/30',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1 text-sm',
};

export function Badge({
  variant = 'default',
  size = 'md',
  children,
  className = '',
}: BadgeProps): JSX.Element {
  return (
    <span
      className={`
        inline-flex items-center justify-center
        font-medium rounded-md
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `.trim()}
    >
      {children}
    </span>
  );
}

/**
 * Mode Badge - Specialized badge for pool mode display
 */
export interface ModeBadgeProps {
  mode: 'gm' | 'pick';
  size?: BadgeSize;
  className?: string;
}

export function ModeBadge({ mode, size = 'md', className = '' }: ModeBadgeProps): JSX.Element {
  const label = mode === 'gm' ? 'GM Mode' : 'Pick Mode';
  const variant = mode === 'gm' ? 'mode-gm' : 'mode-pick';
  
  return (
    <Badge variant={variant} size={size} className={className}>
      {label}
    </Badge>
  );
}

/**
 * Status Badge - Specialized badge for pool/item status
 */
export interface StatusBadgeProps {
  status: 'active' | 'pending' | 'complete' | 'completed' | 'locked' | 'cancelled';
  size?: BadgeSize;
  className?: string;
}

export function StatusBadge({ status, size = 'md', className = '' }: StatusBadgeProps): JSX.Element {
  const normalizedStatus = status === 'completed' ? 'complete' : status;
  
  const labels: Record<string, string> = {
    active: 'Active',
    pending: 'Pending',
    complete: 'Complete',
    locked: 'Locked',
    cancelled: 'Cancelled',
  };
  
  const variants: Record<string, BadgeVariant> = {
    active: 'active',
    pending: 'pending',
    complete: 'complete',
    locked: 'locked',
    cancelled: 'neutral',
  };
  
  return (
    <Badge 
      variant={variants[normalizedStatus] || 'default'} 
      size={size} 
      className={className}
    >
      {labels[normalizedStatus] || status}
    </Badge>
  );
}

/**
 * Outcome Badge - BSL-compliant outcome indicator (no red/green)
 */
export interface OutcomeBadgeProps {
  outcome: 'won' | 'lost' | 'pending';
  size?: BadgeSize;
  className?: string;
}

export function OutcomeBadge({ outcome, size = 'md', className = '' }: OutcomeBadgeProps): JSX.Element {
  // BSL Compliance: Neutral colors only
  const styles: Record<string, string> = {
    won: 'bg-blue-600/20 text-blue-400 border border-blue-500/30',
    lost: 'bg-slate-600/20 text-slate-400 border border-slate-500/30',
    pending: 'bg-amber-600/20 text-amber-400 border border-amber-500/30',
  };
  
  const labels: Record<string, string> = {
    won: 'Won',
    lost: 'Lost',
    pending: 'Pending',
  };
  
  return (
    <span
      className={`
        inline-flex items-center justify-center
        font-medium rounded-md
        ${styles[outcome]}
        ${sizeStyles[size]}
        ${className}
      `.trim()}
    >
      {labels[outcome]}
    </span>
  );
}

export default Badge;
